import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useLocationStore } from '@/store/location';
import { useToast } from '@/components/common/Toast';
import { locationService } from '@/services/location';

interface AddressFormData {
  alias: string;
  detailAddress: string;
  deliveryInstructions: string;
  additionalInfo: string;
}

export function AddressDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedAddress } = location.state || {};
  
  const { addSavedAddress } = useLocationStore();
  
  const [formData, setFormData] = useState<AddressFormData>({
    alias: '',
    detailAddress: '',
    deliveryInstructions: '',
    additionalInfo: ''
  });
  
  const { showToast, ToastContainer } = useToast();
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // 선택된 주소가 없으면 주소 선택 페이지로 리다이렉트
  useEffect(() => {
    if (!selectedAddress) {
      navigate('/address-select', { replace: true });
    }
  }, [selectedAddress, navigate]);

  // 뒤로가기 버튼
  const handleBack = () => {
    navigate(-1);
  };

  // 입력값 변경 핸들러
  const handleInputChange = (field: keyof AddressFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 주소 추가 버튼
  const handleAddAddress = async () => {
    // 필수 필드 검증 - 닉네임만 필수
    if (!formData.alias.trim()) {
      alert('주소의 닉네임을 입력해주세요.');
      return;
    }

    try {
      // Geocoding을 통해 좌표 얻기
      const fullAddress = formData.detailAddress.trim() 
        ? `${selectedAddress.roadAddress} ${formData.detailAddress.trim()}`
        : selectedAddress.roadAddress;
      
      const coordinate = await locationService.geocode(fullAddress);
      
      // 좌표 검증
      if (!coordinate || coordinate.latitude === 0 || coordinate.longitude === 0) {
        // 좌표가 0으로 반환된 경우도 주소 저장은 계속 진행
      }
      
      // 주소 저장
      await addSavedAddress({
        alias: formData.alias.trim(),
        address: selectedAddress.roadAddress,
        jibunAddress: selectedAddress.jibunAddress,
        detailAddress: formData.detailAddress.trim(),
        deliveryInstructions: formData.deliveryInstructions.trim() || undefined,
        additionalInfo: formData.additionalInfo.trim() || undefined,
        coordinate: coordinate
      });

      // 토스트 메시지 표시
      showToast('주소가 추가되었습니다');
      
      // 2초 후 주소 선택 페이지로 돌아가기
      setTimeout(() => {
        navigate('/address-select');
      }, 2000);
    } catch (error) {
      console.error('Failed to add address:', error);
      alert('주소 추가에 실패했습니다. 다시 시도해주세요.');
    }
  };

  if (!selectedAddress) {
    return null; // 로딩 중이거나 리다이렉트 중
  }

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <header className="flex items-center justify-center px-4 pt-5 pb-4 relative">
        <button
          onClick={handleBack}
          className="absolute left-4 p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18L9 12L15 6"
              stroke="#222222"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        
        <h1 className="text-2xl font-bold text-[#222222]">상세주소 설정</h1>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 pb-6">
        {/* 현재 위치 주소 표시 */}
        <div className="mb-6">
          <div className="text-base font-bold text-[#000000] mb-3">
            현 위치 도로명 주소
          </div>
          <div className="text-base font-bold text-[#000000] mb-3">
            {selectedAddress.roadAddress}
          </div>
          <div className="text-sm font-medium text-[#000000] mb-3">
            현 위치 지번 주소
          </div>
          <div className="text-sm font-medium text-[#000000]">
            {selectedAddress.jibunAddress}
          </div>
        </div>

        {/* 닉네임 입력 (필수) */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={formData.alias}
              onChange={(e) => handleInputChange('alias', e.target.value)}
              onFocus={() => setFocusedField('alias')}
              onBlur={() => setFocusedField(null)}
              placeholder="사용하실 주소의 닉네임을 입력해주세요."
              className={cn(
                "w-full h-8 pl-3 pr-8 bg-[#F5F5F5] border rounded text-base text-[#222222] transition-colors",
                focusedField === 'alias' ? "border-[#51C854]" : "border-[#51C854]"
              )}
            />
            {/* 필수 표시 별표 */}
            <div className="absolute left-1 top-0 w-2 h-2 bg-[#51C854]" style={{
              clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
            }} />
          </div>
        </div>

        {/* 상세 주소 입력 (선택) */}
        <div className="mb-4">
          <input
            type="text"
            value={formData.detailAddress}
            onChange={(e) => handleInputChange('detailAddress', e.target.value)}
            onFocus={() => setFocusedField('detailAddress')}
            onBlur={() => setFocusedField(null)}
            placeholder="상세 주소를 입력해주세요. (선택)"
            className={cn(
              "w-full h-8 px-3 bg-[#F5F5F5] border border-[#606060] rounded text-base transition-colors",
              focusedField === 'detailAddress' ? "text-[#222222]" : "text-[#606060]"
            )}
          />
        </div>

        {/* 찾아오는 길 안내 */}
        <div className="mb-4">
          <input
            type="text"
            value={formData.deliveryInstructions}
            onChange={(e) => handleInputChange('deliveryInstructions', e.target.value)}
            onFocus={() => setFocusedField('deliveryInstructions')}
            onBlur={() => setFocusedField(null)}
            placeholder="찾아오는 길 안내(선택)"
            className={cn(
              "w-full h-8 px-3 bg-[#F5F5F5] border border-[#606060] rounded text-base transition-colors",
              focusedField === 'deliveryInstructions' ? "text-[#222222]" : "text-[#606060]"
            )}
          />
        </div>

        {/* 배달 시 추가정보 */}
        <div className="mb-6">
          <input
            type="text"
            value={formData.additionalInfo}
            onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
            onFocus={() => setFocusedField('additionalInfo')}
            onBlur={() => setFocusedField(null)}
            placeholder="공동현관 비밀번호 등 배달 시 추가정보(선택)"
            className={cn(
              "w-full h-8 px-3 bg-[#F5F5F5] border border-[#606060] rounded text-base transition-colors",
              focusedField === 'additionalInfo' ? "text-[#222222]" : "text-[#606060]"
            )}
          />
        </div>


        {/* 신규 주소 추가 버튼 */}
        <button
          onClick={handleAddAddress}
          className="w-full h-8 bg-[#DEDEDE] rounded-xl flex items-center justify-center transition-all hover:bg-gray-300 active:bg-gray-400"
        >
          <span className="text-xl font-semibold text-[#222222]">신규 주소 추가</span>
        </button>
      </main>

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
}